import { DELIVERABLE_MIME_PREFERENCES } from "@cuekards/core/recording";

/**
 * What this browser has to be able to do, checked before anything else happens
 * (web ticket 17, stories 2 and 3).
 *
 * The gate exists because of what the alternative costs. Without it, a visitor on
 * the wrong browser grants a camera permission, pastes a script, presses record
 * and finds out at the end that there was never going to be a file — and what
 * they are told at that point is whichever API happened to throw first.
 * "Unsupported browser" is barely better: it wastes the visit and tells them
 * nothing they can act on. So the check is up front and it names the missing
 * capability and what to do about it.
 *
 * **Two functions and a split that is the whole design.** {@link browserFacts}
 * reads a `navigator` and answers six yes-or-no questions; nothing else in here
 * touches a platform object. {@link missingCapabilities} turns those answers into
 * what a person is told. Every decision — who is refused, what they are shown,
 * and in what order — is therefore a pure function over six booleans, and is
 * tested as one.
 */

/** The browsers this is known to work in, named because "a supported browser" is
 * not an instruction anybody can follow. */
export const SUPPORTED_BROWSERS = ["Chrome", "Edge", "Brave"] as const;

/** What a browser needs to be able to do, as answers rather than as API names. */
export interface BrowserFacts {
  /** A phone or a tablet, which is the wrong machine rather than an old one. */
  mobile: boolean;
  /** Workers, which every controller and the recogniser live in. */
  workers: boolean;
  /** A camera and a microphone can be opened at all — false in an insecure
   * context, which is a deployment mistake and not an old browser. */
  camera: boolean;
  /** `MediaRecorder` exists. */
  recorder: boolean;
  /** …and records at least one container a tab can hand over. */
  container: boolean;
  /** Origin-private storage, which is where a take's bytes go while it is being
   * recorded and what makes crash recovery possible (web ticket 13). */
  privateStorage: boolean;
}

/** One thing this browser cannot do, in the three parts a visitor needs. */
export interface MissingCapability {
  /** Which capability, specifically. */
  what: string;
  /** Why cuekards needs it — so that the refusal reads as a reason rather than as
   * a rule. */
  why: string;
  /** What to do about it. An imperative, always. */
  do: string;
}

/**
 * Where to go instead, said once so that every refusal says the same thing.
 *
 * The version is the bundle's own build target — `scripts/site.ts` builds for
 * `chrome120` — rather than a number anybody guessed. Below it the page would not
 * parse, whatever else the browser could do.
 *
 * **What is refused is a missing capability, never a brand.** A browser that is
 * not on this list and can do all six things is let through, which is deliberate:
 * the spec supports desktop Chromium because that is where the capabilities are,
 * and a Firefox that records WebM gets a working prompter and an honest sentence
 * about the container it produced (`containerExplanation`) rather than a door shut
 * on the name of its engine. The list is where to *go* when something is missing,
 * not a list of who is allowed in.
 *
 * Exported since seo ticket 05, which answers "which browsers does it work in?"
 * on the landing page. Somebody reading that answer and somebody who has been
 * refused by the gate are asking one question, so they are told one sentence —
 * and the version number in it is derived from the bundle's build target rather
 * than guessed, which is the half a second copy would get wrong first.
 */
export const A_SUPPORTED_BROWSER =
  `Use a recent desktop ${SUPPORTED_BROWSERS.join(", ")} ` +
  `or another Chromium-based browser — version 120 or newer.`;

/**
 * Why a phone is the wrong machine, in one sentence (seo ticket 05).
 *
 * Exported because the sentence is now said twice and written once: the gate says
 * it to somebody who has already opened cuekards on a phone, and the landing
 * page's FAQ says it to somebody deciding whether to try. That is the same
 * arrangement `PRIVACY_SHORT` has across `/how-it-works` and the green room — two
 * renderings of one constant. Two copies of the sentence would be the one that
 * drifts. (It had a third on `/` until signal-and-paper ticket 08 put the claims
 * themselves on that page, and a summary of four claims beside three of them is
 * exactly what this arrangement is against. The green room's is a second *copy*,
 * in `site-links.tsx`, that `site-links.test.ts` holds to the first — a bundle
 * boundary the gate's sentence does not have to cross.)
 */
export const NOT_A_PHONE =
  "cuekards fills the monitor beside your camera lens, records a take of " +
  "any length, and saves the file where you choose. A phone or tablet " +
  "can do none of the three.";

/**
 * Everything this browser cannot do, or an empty list when it can do all of it.
 *
 * Ordered so that the reason to give up reading comes first: a phone is answered
 * on its own, and after that the list runs from the capability whose absence is
 * most likely to be a fixable mistake to the one that means an old browser.
 */
export function missingCapabilities(facts: BrowserFacts): MissingCapability[] {
  /**
   * A phone is turned away on its own terms, and nothing else is listed.
   *
   * Not because the other checks would pass — several of them will not — but
   * because a list of five missing APIs is exactly the "half-working, generic
   * failure" the story is against. The answer is not a different browser on the
   * same device; it is a different device, and there is nothing else worth
   * saying.
   */
  if (facts.mobile) {
    return [
      {
        what: "A desktop or laptop browser",
        why: NOT_A_PHONE,
        do: "Open cuekards on a desktop or laptop in Chrome, Edge or another Chromium-based browser.",
      },
    ];
  }

  const missing: MissingCapability[] = [];

  /**
   * First, because it is the one entry in this list that is usually nobody's
   * browser being old: `navigator.mediaDevices` does not exist in an insecure
   * context, so an `http://` address produces exactly this refusal on a browser
   * that is perfectly capable.
   */
  if (!facts.camera) {
    missing.push({
      what: "Camera and microphone access",
      why:
        "A browser only offers a page a camera over a secure connection, and " +
        "this page was not served over one.",
      do: "Open cuekards at its https address. If you are running it yourself, http://localhost counts as secure and any other http address does not.",
    });
  }

  if (!facts.recorder) {
    missing.push({
      what: "In-browser recording (MediaRecorder)",
      why: "cuekards records your take in the tab. Without this there is no file at the end of it.",
      do: A_SUPPORTED_BROWSER,
    });
  } else if (!facts.container) {
    // Only when there *is* a recorder: `isTypeSupported` cannot be asked of a
    // browser that has no `MediaRecorder`, so reporting both would be two
    // sentences about one cause.
    missing.push({
      what: "A video format this browser can record (MP4 with H.264, or WebM)",
      why:
        "cuekards hands you the file exactly as it was captured — it does not " +
        "convert anything — so it has to record a format you can keep.",
      do: A_SUPPORTED_BROWSER,
    });
  }

  if (!facts.privateStorage) {
    missing.push({
      what: "Private file storage in the browser (origin-private storage)",
      why:
        "Your take is written there as it is recorded, which is what makes a " +
        "closed tab or a crash cost seconds instead of the whole take.",
      do: `${A_SUPPORTED_BROWSER} Some browsers also turn this off in a private window, so try an ordinary one.`,
    });
  }

  if (!facts.workers) {
    missing.push({
      what: "Web workers",
      why:
        "cuekards runs the prompter, the recorder and the speech recogniser in " +
        "separate threads so that one cannot stall another mid-take.",
      do: A_SUPPORTED_BROWSER,
    });
  }

  return missing;
}

/**
 * As much of a browser as the gate asks about.
 *
 * Every member is optional, including `navigator` itself, and that is the point:
 * this type describes what might be there rather than what is, so the probe reads
 * the same way against a browser from 2015 as against a fake.
 */
export interface ProbeTarget {
  navigator?: {
    userAgent?: string;
    /** Client hints, on Chromium only — and the one answer here that is the
     * browser's own rather than a guess about it. */
    userAgentData?: { mobile?: boolean };
    maxTouchPoints?: number;
    mediaDevices?: { getUserMedia?: unknown };
    storage?: { getDirectory?: unknown };
  };
  Worker?: unknown;
  MediaRecorder?: { isTypeSupported?: (mimeType: string) => boolean };
}

/**
 * Read the six facts off a browser.
 *
 * The target is a parameter with `globalThis` as its default, so the tests drive
 * the real function against fabricated browsers rather than a second copy of its
 * rules. Nothing here decides anything; see {@link missingCapabilities}.
 */
export function browserFacts(
  target: ProbeTarget = globalThis as ProbeTarget,
): BrowserFacts {
  const navigator = target.navigator;
  const recorder = typeof target.MediaRecorder?.isTypeSupported === "function";
  return {
    mobile: looksMobile(navigator),
    workers: target.Worker !== undefined,
    camera: typeof navigator?.mediaDevices?.getUserMedia === "function",
    recorder,
    container: recorder && recordsSomethingDeliverable(target.MediaRecorder!),
    privateStorage: typeof navigator?.storage?.getDirectory === "function",
  };
}

/**
 * Whether this is a phone or a tablet.
 *
 * Three answers in order of how much they can be trusted. Client hints are the
 * browser's own statement and settle it. Failing that, the user agent string
 * names itself as mobile, which every phone browser does. Failing *that*, there
 * is one case worth a sniff: an iPad reports itself as a Macintosh and means it,
 * and the only thing separating it from a Mac is that a Mac has no touchscreen.
 * A touchscreen laptop is not caught by that rule, because a Chromium on one
 * sends client hints and answers `mobile: false` before it is reached.
 */
function looksMobile(navigator: ProbeTarget["navigator"]): boolean {
  if (typeof navigator?.userAgentData?.mobile === "boolean") {
    return navigator.userAgentData.mobile;
  }
  const userAgent = navigator?.userAgent ?? "";
  if (/Android|iPhone|iPod|iPad|Mobile|Silk|Kindle|Opera Mini/u.test(userAgent)) {
    return true;
  }
  return /Macintosh/u.test(userAgent) && (navigator?.maxTouchPoints ?? 0) > 1;
}

/**
 * Whether the recorder will produce anything this host can hand over.
 *
 * The *deliverable* list rather than the capture list, because a tab has no
 * converter: what it records is what the user gets (web ticket 01, and
 * `DELIVERABLE_MIME_PREFERENCES` for the ordering and why). A browser that
 * supports none of it could still scroll a script, but it could never produce a
 * take, and a prompter that cannot film is not this product.
 */
function recordsSomethingDeliverable(
  recorder: NonNullable<ProbeTarget["MediaRecorder"]>,
): boolean {
  return DELIVERABLE_MIME_PREFERENCES.some((mimeType) => {
    try {
      return recorder.isTypeSupported!(mimeType) === true;
    } catch {
      // A browser that throws when asked is a browser that does not support it.
      return false;
    }
  });
}
